import { useCompacto } from '../shell/compacto.ts'
import { evaluateFilter, type SavedFilter } from '@orbind/core'
import {
  Alert,
  Button,
  Checkbox,
  FieldBlock,
  FilterBar,
  Input,
  LoadingState,
  Modal,
  Select,
  TagSelect,
  Textarea,
  type FilterField,
  useToast,
} from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { api, apiBase } from '../lib/api.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { useT } from '../lib/i18n.tsx'
import { useSession } from '../lib/session.tsx'
import { useWorkItemDetail } from '../features/work-item/api.ts'
import { WorkItemDetailView } from '../features/work-item/WorkItemDetail.tsx'
import { SomenteNesteQuadro, VisibilidadeDosNovosItens } from '../features/kanban/VisibilidadeDosNovosItens.tsx'
import { KB_BG_COLORS, KanbanBoardPage } from './KanbanBoardPage.tsx'
import { EscolherImagemDeFundo } from '../features/kanban/EscolherImagemDeFundo.tsx'
import { urlDoFundo } from '../features/kanban/fundo-de-quadro.ts'
import { CHAVE_ULTIMO_QUADRO } from '../features/kanban/proximo-quadro.ts'
import '../features/work-item/wi.css'
import { WorkspaceBar } from '../features/workspace/WorkspaceBar.tsx'
import { WorkItemsPage } from '../features/work-item/WorkItemsPage.tsx'
import './kanban.css'

const FILTERS_KEY = 'orbind.kanbans.filters.v2'

/** Campos filtraveis do quadro — a "tabela" desta funcionalidade. */
const BOARD_FILTER_FIELDS: readonly FilterField[] = [
  { key: 'name', label: 'Nome do quadro', type: 'text', placeholder: 'Texto do nome' },
  { key: 'areaIds', label: 'Área', type: 'select', placeholder: 'Buscar áreas…' },
  { key: 'ownerMemberId', label: 'Dono do quadro', type: 'select', placeholder: 'Buscar pessoas…' },
  { key: 'situation', label: 'Situação', type: 'select', placeholder: 'Ativo ou inativo' },
  { key: 'laneCount', label: 'Quantidade de lanes', type: 'number' },
  { key: 'itemTypeCount', label: 'Tipos de item', type: 'number' },
]
/** Ultimo quadro aberto: reabrir o explorer volta para ele, ja selecionado. */
// a mesma chave que o quadro atualiza ao ser excluído (SPEC-20261027-1958-excluir-quadro-vai-para-o-primeiro)
const LAST_BOARD_KEY = CHAVE_ULTIMO_QUADRO

/**
 * Quadros — split view com o explorer (regiao `listBoards` do prototipo):
 * linhas com icone fa-columns, nome + meta, badge a direita e rodape do
 * painel; o quadro selecionado abre ao lado e o explorer recolhe (salvo pin).
 */
export function KanbansPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { id: selectedId = null, workItemId = null } = useParams()
  /**
   * O nó "Itens de Trabalho" do explorer (SPEC-20261003-1059): sem quadro
   * selecionado, a área de trabalho mostra TODOS os itens, na mesma tabela.
   */
  const modoItens = location.pathname === '/kanbans/items' || location.pathname.startsWith('/kanbans/items/')
  /** Onde o cabeçalho da lista de Itens é desenhado, acima do painel. */
  const [cabecalhoDosItens, setCabecalhoDosItens] = useState<HTMLDivElement | null>(null)

  /**
   * Para onde o item volta ao fechar, e onde abre o próximo
   * (SPEC-20261006-1351-item-abre-ao-lado-do-explorer): de onde se veio. Aberto
   * pela lista de Itens, volta para ela; aberto por um quadro, volta para o
   * quadro — com o cartão marcado, para a pessoa não ter de procurá-lo.
   */
  // `${baseDoItem}/items/${id}`: /kanbans/items/<id> ou /kanbans/<quadro>/items/<id>
  const baseDoItem = modoItens ? '/kanbans' : `/kanbans/${selectedId}`
  /*
    A CADEIA de itens abertos (SPEC-20261026-2338-fechar-item-volta-em-cadeia, pedido do Cristian:
    "ao fechar o filho deveria voltar para o item de onde ele foi aberto, em
    cadeia"). Cada item aberto DE DENTRO de outro guarda, no estado da
    navegação, os itens de onde se veio; fechar volta um nível, e só o primeiro
    da cadeia volta ao quadro (ou à lista). Antes fechar o filho caía direto no
    quadro.
  */
  const cadeia = ((location.state as { cadeiaDeItens?: unknown } | null)?.cadeiaDeItens ?? []) as string[]
  const abrirOutroItem = (proximoId: string) => {
    if (!workItemId || proximoId === workItemId) return
    navigate(`${baseDoItem}/items/${proximoId}`, { state: { cadeiaDeItens: [...cadeia, workItemId] } })
  }
  const voltarDoItem = () => {
    const anterior = cadeia.at(-1)
    if (anterior) {
      navigate(`${baseDoItem}/items/${anterior}`, { state: { cadeiaDeItens: cadeia.slice(0, -1) } })
      return
    }
    navigate(modoItens ? '/kanbans/items' : `/kanbans/${selectedId}?card=${workItemId}`)
  }
  const t = useT()
  const toast = useToast()
  const queryClient = useQueryClient()

  const session = useSession()
  const bootstrap = useBootstrap()

  const [search, setSearch] = useState('')
  const [explorerWidth, setExplorerWidth] = useState(340)
  const [explorerCollapsed, setExplorerCollapsed] = useState(false)
  // no celular o explorador nunca fica recolhido: ele se reveza com o conteúdo
  const compacto = useCompacto()
  // Pin do explorer: por padrao ele RECOLHE ao selecionar um quadro (area
  // maxima para o Kanban); fixado pelo pin, permanece aberto.
  const [pinned, setPinned] = useState(() => localStorage.getItem('orbind.kanbans.pin') === '1')
  const togglePin = () =>
    setPinned((current) => {
      const next = !current
      localStorage.setItem('orbind.kanbans.pin', next ? '1' : '0')
      return next
    })
  const explorerRef = useRef<HTMLElement>(null)
  const resizing = useRef(false)

  // modal de criacao: titulo, descricao, area(s) — minimo uma — e dono
  // (default = membro logado, pode ser trocado)
  const [createOpen, setCreateOpen] = useState(false)
  /** Novo quadro em modo AVANÇADO (SPEC-20261027-0943-novo-quadro-basico-e-avancado): mostra áreas, tipos, dono, fluxos e exclusividade. */
  const [avancado, setAvancado] = useState(false)
  const [form, setForm] = useState({
    name: '',
    description: '',
    areaIds: [] as string[],
    ownerMemberId: '',
    workItemTypeIds: [] as string[],
    backgroundColor: null as string | null,
    backgroundImageUrl: null as string | null,
    /** Fluxos do quadro: ao criar, nasce UMA lane por etapa de cada um. */
    flowIds: [] as string[],
    /** Itens criados no quadro nascem privados (SPEC-20261007-0934-visibilidade-padrao-do-quadro). */
    newItemsPrivate: false,
    /** Itens criados no quadro aparecem só nele (SPEC-20261007-1538-quadros-do-item). */
    newItemsExclusive: false,
    /**
     * Imagem escolhida DO COMPUTADOR (SPEC-20261027-1840-biblioteca-de-fundos-e-imagem-do-quadro): o quadro
     * novo ainda não tem onde guardá-la, então ela sobe logo depois de criado.
     */
    arquivoDeFundo: null as File | null,
    previaDoArquivo: null as string | null,
  })
  /** Painel do botão "Imagem" das cores de fundo aberto. */
  const [painelDeImagem, setPainelDeImagem] = useState(false)

  // fluxos do workspace para a escolha na criacao (403 = plano sem fluxos)
  const fluxos = useQuery({
    queryKey: ['flows'],
    queryFn: async () => (await api.flows.$get()).json(),
    enabled: createOpen,
    retry: false,
  })

  // filtros nomeados (persistidos por usuario no navegador)
  const [quickFilters, setQuickFilters] = useState<SavedFilter[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(FILTERS_KEY) ?? '[]') as SavedFilter[]
    } catch {
      return []
    }
  })
  const [activeFilterId, setActiveFilterId] = useState<string | null>(null)

  const storeFilters = (list: SavedFilter[]) => {
    setQuickFilters(list)
    localStorage.setItem(FILTERS_KEY, JSON.stringify(list))
  }

  const openCreate = () => {
    setForm({
      name: '',
      description: '',
      areaIds: session.companyAreaId ? [session.companyAreaId] : [],
      ownerMemberId: session.id,
      // todos os tipos participam por padrao: quadro sem tipo nasce vazio
      workItemTypeIds: [],
      backgroundColor: KB_BG_COLORS[0] ?? null,
      backgroundImageUrl: null,
      flowIds: [],
      newItemsPrivate: false,
      newItemsExclusive: false,
      arquivoDeFundo: null,
      previaDoArquivo: null,
    })
    setPainelDeImagem(false)
    setAvancado(false)
    setCreateOpen(true)
  }

  const boards = useQuery({
    queryKey: ['kanbans'],
    queryFn: async () => (await api.kanbans.$get()).json(),
  })

  // detalhe do item aberto pelo quadro (rota /kanbans/:id/items/:workItemId)
  const itemDetail = useWorkItemDetail(workItemId)

  const favorite = useMutation({
    mutationFn: async (input: { boardId: string; isFavorite: boolean }) =>
      (
        await api.kanbans[':id'].favorite.$put({
          param: { id: input.boardId },
          json: { isFavorite: input.isFavorite },
        })
      ).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kanbans'] }),
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao favoritar o quadro.' }),
  })

  const createBoard = useMutation({
    mutationFn: async () =>
      (
        await api.kanbans.$post({
          json: {
            name: form.name.trim(),
            description: form.description.trim() === '' ? null : form.description.trim(),
            areaIds: form.areaIds,
            ownerMemberId: form.ownerMemberId,
            workItemTypeIds: form.workItemTypeIds,
            backgroundColor: form.backgroundImageUrl || form.arquivoDeFundo ? null : form.backgroundColor,
            // o arquivo do computador sobe depois (onSuccess)
            backgroundImageUrl: form.arquivoDeFundo ? null : form.backgroundImageUrl,
            flowIds: form.flowIds,
            newItemsPrivate: form.newItemsPrivate,
            newItemsExclusive: form.newItemsExclusive,
          },
        })
      ).json(),
    onSuccess: async (data) => {
      // imagem do computador: sobe agora que o quadro existe, e já vira o fundo
      if (data.board && form.arquivoDeFundo) {
        try {
          const corpo = new FormData()
          corpo.append('file', form.arquivoDeFundo)
          const r = await fetch(`${apiBase()}/kanbans/${data.board.id}/backgrounds`, {
            method: 'POST',
            headers: { authorization: `Bearer ${localStorage.getItem('orbind.token') ?? ''}` },
            body: corpo,
          })
          if (!r.ok) throw new Error()
        } catch {
          toast.push({
            tone: 'danger',
            message: 'O quadro foi criado, mas a imagem de fundo não subiu. Tente de novo pela personalização do quadro.',
          })
        }
      }
      if (form.previaDoArquivo) URL.revokeObjectURL(form.previaDoArquivo)
      queryClient.invalidateQueries({ queryKey: ['kanbans'] })
      setCreateOpen(false)
      if (data.board) navigate(`/kanbans/${data.board.id}`)
      toast.push({ tone: 'success', message: 'Quadro criado. Configure as lanes pela engrenagem.' })
    },
    onError: (error) =>
      toast.push({ tone: 'danger', message: error instanceof Error ? error.message : 'Falha ao criar o quadro.' }),
  })

  const lastBoardId = useRef<string | null>(localStorage.getItem(LAST_BOARD_KEY))
  useEffect(() => {
    if (selectedId !== null) {
      lastBoardId.current = selectedId
      localStorage.setItem(LAST_BOARD_KEY, selectedId)
    }
  }, [selectedId])

  /**
   * Clicar no icone de Quadros na barra leva a /kanbans (sem id): o painel
   * explorer SEMPRE reabre — e, se havia um quadro aberto, ele CONTINUA
   * aberto e seleccionado (o fundo do quadro tambem permanece). O quadro so
   * troca ao clicar em outro no explorer.
   */
  useEffect(() => {
    if (selectedId !== null) return
    setExplorerCollapsed(false)
    // pedir os ITENS e uma escolha: reabrir o ultimo quadro por cima dela
    // desfaria o clique da pessoa (SPEC-20261003-1059)
    if (modoItens) return
    const last = lastBoardId.current
    if (last && (boards.data?.boards ?? []).some((b) => b.id === last)) {
      navigate(`/kanbans/${last}`, { replace: true })
    }
  }, [selectedId, modoItens, location.key, boards.data, navigate])

  // ao reabrir o painel, a linha do quadro aberto entra em vista
  useEffect(() => {
    if (explorerCollapsed) return
    explorerRef.current?.querySelector('.wi-row--on')?.scrollIntoView({ block: 'nearest' })
  }, [explorerCollapsed, selectedId])

  // clique x arraste no divisor (SPEC-20260916-0149): mexeu = redimensionou;
  // soltou sem mexer = recolhe/expande
  const arrasteMoveu = useRef(false)
  useEffect(() => {
    const onMove = (event: MouseEvent) => {
      if (!resizing.current) return
      arrasteMoveu.current = true
      const left = explorerRef.current?.getBoundingClientRect().left ?? 0
      setExplorerWidth(Math.max(240, Math.min(520, event.clientX - left)))
    }
    const onUp = () => {
      resizing.current = false
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const labelOf = (key: string) => {
    const label = t(key)
    return label === key ? key : label
  }

  const activeFilter = quickFilters.find((f) => f.id === activeFilterId) ?? null
  const rows = (boards.data?.boards ?? []).filter((b) => {
    const label = labelOf(b.nameTranslationKey)
    if (!label.toLowerCase().includes(search.trim().toLowerCase())) return false
    if (!activeFilter) return true
    // achata o quadro nos campos declarados em BOARD_FILTER_FIELDS
    return evaluateFilter(
      {
        name: label,
        areaIds: b.areaIds,
        ownerMemberId: b.ownerMemberId,
        situation: b.isActive ? 'Ativo' : 'Inativo',
        laneCount: b.laneCount,
        itemTypeCount: b.itemTypeCount,
      },
      activeFilter,
    )
  })

  /** Opcoes que existem na base para o campo escolhido no construtor. */
  const loadValueOptions = (fieldKey: string, query: string) => {
    const match = (label: string) => label.toLowerCase().includes(query.trim().toLowerCase())
    if (fieldKey === 'areaIds') {
      return (bootstrap.data?.areas ?? [])
        .filter((a) => a.isActive && match(labelOf(a.nameTranslationKey)))
        .map((a) => ({ value: a.id, label: labelOf(a.nameTranslationKey) }))
    }
    if (fieldKey === 'ownerMemberId') {
      return (bootstrap.data?.members ?? [])
        .filter((c) => c.isActive && match(c.name))
        .map((c) => ({ value: c.id, label: c.name }))
    }
    if (fieldKey === 'situation') {
      return [
        { value: 'Ativo', label: 'Ativo' },
        { value: 'Inativo', label: 'Inativo (rascunho)' },
      ].filter((o) => match(o.label))
    }
    if (fieldKey === 'name') {
      // sugestoes com os nomes que ja existem na base
      return (boards.data?.boards ?? [])
        .map((b) => labelOf(b.nameTranslationKey))
        .filter((name, index, all) => all.indexOf(name) === index && match(name))
        .map((name) => ({ value: name, label: name }))
    }
    return []
  }

  return (
    <div className="ob-root wi-split">
      {explorerCollapsed && !compacto ? (
        // recolhido: a faixa inteira é o botão de expandir (só se vê no hover)
        <div className="wi-resizer wi-resizer--kb wi-resizer--recolhido" title="Mostrar explorer" onClick={() => setExplorerCollapsed(false)}>
          <button type="button" className="wi-resizer__toggle" aria-label="Mostrar explorer" tabIndex={-1}>
            <i className="fas fa-chevron-right" aria-hidden style={{ fontSize: 9 }} />
          </button>
        </div>
      ) : (
        <aside ref={explorerRef} className="wi-explorer" style={{ width: explorerWidth }}>
          <WorkspaceBar />
          <div className="wi-explorer__head">
            <h1 className="wi-explorer__title">Quadros</h1>
            <button
              type="button"
              className={`wi-explorer__filter${pinned ? ' kb__pin--on' : ''}`}
              aria-label={pinned ? 'Desafixar explorer' : 'Fixar explorer'}
              title={pinned ? 'Desafixar: recolhe ao selecionar um quadro' : 'Fixar o explorer aberto'}
              onClick={togglePin}
            >
              <i className="fas fa-thumbtack" aria-hidden style={{ fontSize: 12, transform: pinned ? 'none' : 'rotate(45deg)' }} />
            </button>
            <button type="button" className="wi-explorer__create" aria-label="Criar" title="Novo quadro" onClick={openCreate}>
              <i className="fas fa-plus" aria-hidden style={{ fontSize: 12 }} />
            </button>
          </div>

          <div className="wi-explorer__searchwrap">
            <div className="wi-explorer__searchbox">
              <i className="fas fa-search" aria-hidden />
              <input
                className="wi-explorer__search"
                type="search"
                placeholder="Buscar quadros…"
                aria-label="Buscar quadros"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* filtros nomeados: botoes arredondados + "+" que abre o construtor */}
          <FilterBar
            className="kb__qf"
            fields={BOARD_FILTER_FIELDS}
            filters={quickFilters}
            activeFilterId={activeFilterId}
            onActivate={setActiveFilterId}
            onChange={storeFilters}
            loadValueOptions={loadValueOptions}
          />

          <div className="wi-explorer__list">
            {/*
              ITENS DE TRABALHO vem acima de tudo, inclusive dos favoritos
              (pedido do Cristian, 2026-10-03): é a visão sem recorte, de onde
              se enxerga o trabalho todo. Os quadros abaixo são recortes dela.
            */}
            <button
              type="button"
              className={`wi-row kb__noitens${modoItens ? ' wi-row--on' : ''}`}
              onClick={() => {
                navigate('/kanbans/items')
                if (!pinned) setExplorerCollapsed(true)
              }}
            >
              <span className="kb__explorericon">
                <i className="fas fa-tasks" aria-hidden style={{ fontSize: 13 }} />
              </span>
              <span className="wi-row__main">
                <span className="wi-row__top">
                  <span className="wi-row__title">Itens de Trabalho</span>
                </span>
                <span className="wi-row__bottom">
                  <span className="wi-row__code">todos os itens, sem recorte de quadro</span>
                </span>
              </span>
            </button>

            {boards.isPending ? (
              <div style={{ padding: 14 }}>
                <LoadingState rows={3} />
              </div>
            ) : (
              [
                { label: "Favoritos", items: rows.filter((b) => b.isFavorite) },
                { label: rows.some((b) => b.isFavorite) ? "Outros quadros" : null, items: rows.filter((b) => !b.isFavorite) },
              ].map((section) =>
                section.items.length === 0 ? null : (
                  <div key={section.label ?? "all"}>
                    {section.label ? <div className="kb__explorersection">{section.label}</div> : null}
                    {section.items.map((board) => (
                      <button
                        key={board.id}
                        type="button"
                        className={`wi-row${board.id === selectedId ? " wi-row--on" : ""}`}
                        onClick={() => {
                          // ao selecionar um quadro no explorer, revalida os
                          // dados dele para pegar mudancas feitas em outro lugar
                          // (inclusive ao re-selecionar o quadro ja aberto, que
                          // nao remonta a KanbanBoardPage por causa da key)
                          queryClient.invalidateQueries({ queryKey: ['kanban', board.id] })
                          navigate(`/kanbans/${board.id}`)
                          // padrao: recolhe apos selecionar; o pin mantem aberto
                          if (!pinned) setExplorerCollapsed(true)
                        }}
                      >
                        <span className="kb__explorericon">
                          <i className="fas fa-columns" aria-hidden style={{ fontSize: 13 }} />
                        </span>
                        <span className="wi-row__main">
                          <span className="wi-row__top">
                            <span className="wi-row__title">{labelOf(board.nameTranslationKey)}</span>
                          </span>
                          <span className="wi-row__bottom">
                            <span className="wi-row__code">
                              {board.laneCount} {board.laneCount === 1 ? "lane" : "lanes"} ·{" "}
                              {board.itemTypeCount === 0
                                ? "todos os tipos"
                                : `${board.itemTypeCount} ${board.itemTypeCount === 1 ? "tipo de item" : "tipos de item"}`}
                            </span>
                          </span>
                        </span>
                        <span
                          className={`kb__favstar${board.isFavorite ? " kb__favstar--on" : ""}`}
                          role="button"
                          tabIndex={0}
                          aria-label={board.isFavorite ? "Remover dos favoritos" : "Marcar como favorito"}
                          title={board.isFavorite ? "Remover dos favoritos" : "Marcar como favorito"}
                          onClick={(e) => {
                            e.stopPropagation()
                            favorite.mutate({ boardId: board.id, isFavorite: !board.isFavorite })
                          }}
                        >
                          <i className={board.isFavorite ? "fas fa-star" : "far fa-star"} aria-hidden />
                        </span>
                      </button>
                    ))}
                  </div>
                ),
              )
            )}
            {boards.data && rows.length === 0 ? (
              <div className="wi-empty" style={{ padding: '44px 24px' }}>
                <div className="wi-empty__icon">
                  <i className="fas fa-columns" aria-hidden />
                </div>
                <span className="wi-empty__title">Nenhum quadro</span>
                <span className="wi-empty__text">Crie um quadro e configure as lanes direto nele.</span>
              </div>
            ) : null}
          </div>

          <div className="kb__explorerfooter">
            {rows.length} {rows.length === 1 ? 'quadro' : 'quadros'}
          </div>
        </aside>
      )}

      {!explorerCollapsed ? (
        <div
          className="wi-resizer wi-resizer--kb"
          title="Clique para recolher · arraste pela borda esquerda para redimensionar"
          // clique em qualquer ponto da faixa recolhe (pedido 2026-09-16 02:00:
          // "se clicar em qualquer região da barra ela abre"); arrastou, não
          onClick={() => {
            if (!arrasteMoveu.current) setExplorerCollapsed(true)
            arrasteMoveu.current = false
          }}
        >
          {/* ALÇA de arrastar (SPEC-20260916-0216): só a borda esquerda da
              faixa, junto da divisão, mostra o cursor ↔ e redimensiona; o
              resto (~75%) fica com o ponteiro normal e só recolhe */}
          <span
            className="wi-resizer__alca"
            aria-hidden
            onMouseDown={() => {
              resizing.current = true
              arrasteMoveu.current = false
              document.body.style.userSelect = 'none'
            }}
          />
          <button
            type="button"
            className="wi-resizer__toggle"
            aria-label="Recolher explorer"
            tabIndex={-1}
          >
            <i className="fas fa-chevron-left" aria-hidden style={{ fontSize: 9 }} />
          </button>
        </div>
      ) : null}

      <div
        style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}
        // clicar em qualquer lugar do quadro recolhe o explorer (o pin mantem)
        onClick={() => {
          if (!pinned && !explorerCollapsed) setExplorerCollapsed(true)
        }}
      >
        {/*
          O ITEM aberto vem antes de tudo: ele ocupa a área de trabalho ao lado
          do explorer, venha da lista de Itens ou de um quadro. Antes o código
          levava para a tela antiga de Itens de Trabalho, com a lista própria
          dela à esquerda — uma tela que o rail já não oferece.
        */}
        {workItemId ? (
          itemDetail.isPending || !bootstrap.data ? (
            <LoadingState variant="detail" />
          ) : itemDetail.data ? (
            <WorkItemDetailView
              detail={itemDetail.data}
              bootstrap={bootstrap.data}
              onOpenItem={abrirOutroItem}
              onBack={voltarDoItem}
              canGoBack
            />
          ) : (
            <div className="wi-detail">
              <Alert tone="danger" title="Não foi possível carregar">
                Verifique o link ou tente novamente.
              </Alert>
            </div>
          )
        ) : modoItens ? (
          /*
            A lista dentro de um PAINEL, como no quadro
            (SPEC-20261006-2128-lista-de-itens-em-painel): a MESMA estrutura
            (`kb` > `kb__listaitens`), e não um visual imitado — o respiro, a
            transparência sobre a imagem de fundo e o que mais o quadro ganhar
            passam a valer aqui sozinhos. Antes a lista encostava nas bordas
            da área de trabalho.
          */
          <div className="kb kb--itens">
            {/* o cabeçalho da lista vem para cá, fora do painel e sobre o
                fundo — como o do quadro (pedido de 2026-10-06) */}
            <div className="kb-itens__cabecalho" ref={setCabecalhoDosItens} />
            <div className="kb__listaitens">
              <WorkItemsPage embutido slotDoCabecalho={cabecalhoDosItens} />
            </div>
          </div>
        ) : selectedId === null ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="wi-empty">
              <div className="wi-empty__icon">
                <i className="fas fa-columns" aria-hidden />
              </div>
              <span className="wi-empty__title">Selecione um quadro</span>
              <span className="wi-empty__text">Escolha um quadro no explorer para abrir.</span>
            </div>
          </div>
        ) : (
          <KanbanBoardPage key={selectedId} />
        )}
      </div>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Novo quadro"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              loading={createBoard.isPending}
              disabled={form.name.trim() === ''}
              onClick={() => createBoard.mutate()}
            >
              Criar quadro
            </Button>
          </>
        }
      >
        {/* kb__novo-form: campos realçados (SPEC-20261027-1910-novo-quadro-campos-realcados) */}
        <div className="kb__novo-form" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/*
            MODO BÁSICO primeiro (SPEC-20261027-0943-novo-quadro-basico-e-avancado, pedido do Cristian):
            título, descrição, se os itens nascem privados ou públicos e a tela
            de fundo. O resto (áreas, tipos, dono, fluxos) fica no AVANÇADO, que só cresce para baixo; os valores padrão
            deles continuam valendo mesmo sem abrir.
          */}
          <FieldBlock label="Título do quadro" required error={form.name.trim() === '' ? '👋 O título do quadro é obrigatório' : null}>
            {({ id, invalid }) => (
              <Input
                id={id}
                invalid={invalid}
                value={form.name}
                placeholder="Ex.: Desenvolvimento de Produto"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            )}
          </FieldBlock>

          <FieldBlock label="Descrição">
            {({ id }) => (
              <Textarea
                id={id}
                value={form.description}
                rows={3}
                placeholder="Para que serve este quadro"
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            )}
          </FieldBlock>

          <FieldBlock label="Itens criados neste quadro nascem" hint="Dá para mudar depois, em Dados do quadro.">
            {() => (
              <VisibilidadeDosNovosItens
                privados={form.newItemsPrivate}
                onChange={(privados) => setForm((f) => ({ ...f, newItemsPrivate: privados }))}
              />
            )}
          </FieldBlock>
          <FieldBlock
            label="Itens criados somente para este quadro"
            // SPEC-20261027-2001-filtro-fixo-do-quadro-exclusivo
            hint={
              form.newItemsExclusive
                ? 'O quadro já nasce com o filtro "Quadros = este quadro": só aparecem os itens incluídos nele. Dá para mudar ou tirar depois, em Dados do quadro.'
                : undefined
            }
          >
            {() => (
              <SomenteNesteQuadro
                exclusivos={form.newItemsExclusive}
                onChange={(exclusivos) => setForm((f) => ({ ...f, newItemsExclusive: exclusivos }))}
              />
            )}
          </FieldBlock>

          {/*
            Tela de fundo (SPEC-20261027-1840-biblioteca-de-fundos-e-imagem-do-quadro, pedido do Cristian):
            as cores e, no fim delas, o botão "Imagem" — biblioteca global,
            imagem do computador ou URL. Sem a área grande simulando o quadro e
            sem o campo de URL embaixo das cores.
          */}
          <FieldBlock label="Tela de fundo">
            {() => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {KB_BG_COLORS.map((color) => {
                    const on = form.backgroundColor === color && !form.backgroundImageUrl && !form.arquivoDeFundo
                    return (
                      <button
                        key={color}
                        type="button"
                        className={`kb__swatch${on ? ' kb__swatch--on' : ''}`}
                        style={{ background: color, position: 'relative' }}
                        aria-label={`Cor de fundo ${color}`}
                        onClick={() =>
                          setForm((f) => ({ ...f, backgroundColor: color, backgroundImageUrl: null, arquivoDeFundo: null, previaDoArquivo: null }))
                        }
                      >
                        {on ? <i className="fas fa-check" aria-hidden style={{ fontSize: 10, color: 'var(--ob-primary)' }} /> : null}
                      </button>
                    )
                  })}
                  {/* no fim das cores, o botão Imagem: mostra a imagem escolhida */}
                  {(() => {
                    const imagem = form.previaDoArquivo ?? (form.backgroundImageUrl ? urlDoFundo(form.backgroundImageUrl) : null)
                    return (
                      <button
                        type="button"
                        className={`kb__swatch kb__swatch--imagem${imagem ? ' kb__swatch--on' : ''}`}
                        style={imagem ? { backgroundImage: `url("${imagem}")`, position: 'relative' } : { position: 'relative' }}
                        aria-label={imagem ? 'Imagem de fundo escolhida — trocar' : 'Escolher uma imagem de fundo'}
                        aria-expanded={painelDeImagem}
                        title="Imagem: biblioteca, do computador ou por URL"
                        onClick={() => setPainelDeImagem((v) => !v)}
                      >
                        <i className={imagem ? 'fas fa-check' : 'far fa-image'} aria-hidden />
                      </button>
                    )
                  })()}
                </div>
                {painelDeImagem ? (
                  <EscolherImagemDeFundo
                    selecionada={form.backgroundImageUrl}
                    onErro={(mensagem) => toast.push({ tone: 'danger', message: mensagem })}
                    onEscolher={(escolha) => {
                      if (form.previaDoArquivo) URL.revokeObjectURL(form.previaDoArquivo)
                      setForm((f) =>
                        escolha.tipo === 'url'
                          ? { ...f, backgroundImageUrl: escolha.url, arquivoDeFundo: null, previaDoArquivo: null }
                          : { ...f, backgroundImageUrl: null, arquivoDeFundo: escolha.arquivo, previaDoArquivo: escolha.previa },
                      )
                      setPainelDeImagem(false)
                    }}
                  />
                ) : null}
              </div>
            )}
          </FieldBlock>

          {avancado ? (
            <div className="kb__novo-avancado" role="group" aria-label="Configurações avançadas">
              <h3 className="kb__novo-avancado-titulo">Configurações avançadas</h3>
              <FieldBlock
                label="Áreas"
                hint="Opcional. Sem área escolhida, o quadro mostra itens de todas as áreas."
              >
                {({ id, invalid }) => (
                  <TagSelect
                    id={id}
                    invalid={invalid}
                    value={form.areaIds}
                    placeholder="Digite para buscar áreas…"
                    options={(bootstrap.data?.areas ?? [])
                      .filter((a) => a.isActive)
                      .map((a) => ({ value: a.id, label: labelOf(a.nameTranslationKey) }))}
                    onChange={(areaIds) => setForm((f) => ({ ...f, areaIds }))}
                  />
                )}
              </FieldBlock>

              <FieldBlock
                label="Tipos de Item de Trabalho que participam"
                hint="Opcional. Sem nenhum tipo escolhido, o quadro mostra itens de todos os tipos."
              >
                {({ id }) => (
                  <TagSelect
                    id={id}
                    value={form.workItemTypeIds}
                    placeholder="Digite para buscar tipos…"
                    options={(bootstrap.data?.workItemTypes ?? [])
                      .filter((x) => x.isActive)
                      .map((x) => ({ value: x.id, label: labelOf(x.nameTranslationKey), icon: x.icon ?? undefined }))}
                    onChange={(workItemTypeIds) => setForm((f) => ({ ...f, workItemTypeIds }))}
                  />
                )}
              </FieldBlock>

              <FieldBlock label="Dono do quadro" required hint="Já vem com você; pode ser trocado por outro membro.">
                {({ id }) => (
                  <Select
                    id={id}
                    value={form.ownerMemberId}
                    options={(bootstrap.data?.members ?? [])
                      .filter((col) => col.isActive)
                      .map((col) => ({ value: col.id, label: col.name }))}
                    onChange={(v) => setForm((f) => ({ ...f, ownerMemberId: v }))}
                  />
                )}
              </FieldBlock>

              {/* fluxos do quadro: escolhendo, o quadro NASCE com uma lane por
                  etapa de cada fluxo (ajustavel depois) — pedido do usuario */}
              {!fluxos.isError && (fluxos.data?.flows ?? []).length > 0 ? (
                <FieldBlock
                  label="Fluxos do quadro"
                  hint="Opcional. O quadro já nasce com uma lane para cada etapa dos fluxos escolhidos — você ajusta depois se quiser."
                >
                  {() => (
                    <div role="group" aria-label="Fluxos do quadro" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(fluxos.data?.flows ?? [])
                        .filter((f) => f.isActive)
                        .map((f) => (
                          <Checkbox
                            key={f.id}
                            label={`${f.name}${f.stepCount > 0 ? ` (${f.stepCount} ${f.stepCount === 1 ? 'etapa' : 'etapas'})` : ' (sem etapas)'}`}
                            checked={form.flowIds.includes(f.id)}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                flowIds: e.target.checked ? [...prev.flowIds, f.id] : prev.flowIds.filter((x) => x !== f.id),
                              }))
                            }
                          />
                        ))}
                    </div>
                  )}
                </FieldBlock>
              ) : null}
            </div>
          ) : null}

          <div className="kb__novo-modo">
            {avancado ? (
              <Button variant="ghost" icon="fas fa-chevron-up" onClick={() => setAvancado(false)}>
                Voltar ao modo básico
              </Button>
            ) : (
              <Button variant="ghost" icon="fas fa-sliders-h" onClick={() => setAvancado(true)}>
                Avançado
              </Button>
            )}
          </div>
        </div>
      </Modal>

    </div>
  )
}
